import { OpenFeatureProvider } from '@openfeature/react-sdk';
import { ProviderEvents, ProviderStatus } from '@openfeature/web-sdk';
import Box from '@risk-smart/themed-cloudscape-components/box';
import ErrorContent from '@risksmart-app/components/src/error-pages/ErrorContent';
import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import Loading from '@risksmart-app/components/src/loading';
import { getEnv } from '@risksmart-app/components/src/utils/environment';
import { toDatadogEnv } from '@risksmart-app/shared/src/utils/datadog-environment';
import * as Sentry from '@sentry/react';
import type { FC, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { ThirdPartyAuth0Context } from 'src/providers/ThirdPartyAuth0Context';

import { flagClient } from './client';
import {
  clearFeatureFlagContext,
  getFeatureFlagBootstrapError,
  setFeatureFlagContext,
} from './openfeature';

interface Props {
  children: ReactNode;
}

type ContextState = 'pending' | 'ready' | 'failed';

/**
 * A provider in one of these states cannot answer a flag evaluation, so the
 * gate refuses to render children. `STALE` is deliberately absent: a stale
 * provider still serves last-known-good cached values, which beats both
 * erroring and silently falling back to the registry default.
 */
const FAILED_STATUSES: ProviderStatus[] = [
  ProviderStatus.ERROR,
  ProviderStatus.FATAL,
];

const PENDING_STATUSES: ProviderStatus[] = [
  ProviderStatus.NOT_READY,
  ProviderStatus.RECONCILING,
];

/**
 * Tracks provider health reactively, so a provider that degrades *after* mount
 * flips the gate rather than leaving live flag reads to quietly resolve
 * defaults.
 */
const useProviderStatus = (): ProviderStatus => {
  const [status, setStatus] = useState<ProviderStatus>(
    () => flagClient.providerStatus
  );

  useEffect(() => {
    const events = [
      ProviderEvents.Ready,
      ProviderEvents.Error,
      ProviderEvents.Stale,
      ProviderEvents.Reconciling,
      ProviderEvents.ContextChanged,
      ProviderEvents.ConfigurationChanged,
    ];
    const handler = (): void => {
      setStatus(flagClient.providerStatus);
    };
    events.forEach((event) => flagClient.addHandler(event, handler));

    // The provider can reach READY between the initial useState and this
    // subscription — resync so the gate never waits on an event already fired.
    handler();

    return () => {
      events.forEach((event) => flagClient.removeHandler(event, handler));
    };
  }, []);

  return status;
};

/**
 * Mounts the OpenFeature React provider and keeps the global evaluation context
 * in sync with the signed-in respondent's organisation.
 *
 * Replaces the portal-local `FeaturesProvider` (RSP-6570), which read
 * `Meta.features` over Apollo. It keeps that provider's **fail-closed**
 * behaviour rather than adopting web's never-throw hook: the portal's only flag
 * is `trpc`, which selects the transport for the whole app, so an unresolved
 * flag has no safe default. Resolving `false` by accident would silently route
 * a cut-over org back onto the GraphQL path being retired — one whose behaviour
 * we stop validating once an org is cut over (ENG-618). So an unreachable
 * provider blocks and then errors; it never degrades to a default.
 *
 * The split of responsibility: this gate owns "can we reach a provider at all",
 * `useIsFeatureFlagEnabled` owns "is flag X on". Children therefore never
 * observe an unresolved provider, which is what lets the hook stay a plain
 * value read.
 *
 * `user.orgKey` is guaranteed present here — `ProtectedLayout` gates on
 * `isUserInOrganization(user)`, which requires `org_id`, before rendering this.
 */
export const FeatureFlagProvider: FC<Props> = ({ children }) => {
  const { user } = useRisksmartUser(ThirdPartyAuth0Context);
  const orgKey = user?.orgKey;
  const userId = user?.userId;
  const tenantName = user?.tenantName;

  const [contextState, setContextState] = useState<ContextState>('pending');
  const status = useProviderStatus();
  const bootstrapError = getFeatureFlagBootstrapError();

  useEffect(() => {
    if (!orgKey) {
      // Signed out / identity not yet resolved — drop any stale targeting so
      // flags never evaluate against a previous respondent's org.
      setContextState('pending');
      clearFeatureFlagContext().catch((error) => {
        Sentry.captureException(error, {
          tags: { component: 'feature-flags' },
        });
      });

      return;
    }

    let cancelled = false;
    setContextState('pending');

    setFeatureFlagContext({
      orgId: orgKey,
      userId: userId || undefined,
      tenant: tenantName || undefined,
      // app→prod parity with the server's DD_ENV; `||` (not `??`) so an empty
      // REACT_APP_ENVIRONMENT also falls back.
      env: toDatadogEnv(getEnv('REACT_APP_ENVIRONMENT', true)) || 'unknown',
    })
      .then(() => {
        if (!cancelled) {
          setContextState('ready');
        }
      })
      .catch((error) => {
        Sentry.captureException(error, {
          tags: { component: 'feature-flags' },
        });
        // Fail closed: without a bound targeting key the provider cannot
        // resolve org-scoped rules, so children must not render.
        if (!cancelled) {
          setContextState('failed');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [orgKey, userId, tenantName]);

  // Clear on unmount only — kept separate from the effect above so an identity
  // change never transiently blanks the context mid-session.
  useEffect(() => {
    return () => {
      void clearFeatureFlagContext();
    };
  }, []);

  if (
    bootstrapError !== null ||
    contextState === 'failed' ||
    FAILED_STATUSES.includes(status)
  ) {
    return (
      <ErrorContent
        title={'Sorry, we couldn’t make that happen'}
        imgSrc={'/errors/rubiks-cube.png'}
        imgAlt={"Rubik's cube indicating a feature-flag configuration issue"}
      >
        <Box variant={'p'}>{'Please try again later'}</Box>
      </ErrorContent>
    );
  }

  if (contextState === 'pending' || PENDING_STATUSES.includes(status)) {
    return <Loading data-loading-reason={'feature-flags'} />;
  }

  return <OpenFeatureProvider>{children}</OpenFeatureProvider>;
};
